import { existsSync, mkdirSync, writeFileSync } from "node:fs";
import { join } from "node:path";

import type { AgentMessage } from "@elyracode/agent-core";
import { type Api, type AssistantMessage, completeSimple, type Model } from "@elyracode/ai";

import { getAgentDir } from "../config.js";

/** Directory where extracted skills are written */
export function getLearnedSkillsDir(): string {
	return join(getAgentDir(), "skills");
}

const SKILL_EXTRACTION_PROMPT = `You are analyzing a coding session to extract ONE reusable skill.

A skill is a repeatable procedure the user taught or demonstrated during this session: a workflow, a recipe, a sequence of steps that would be valuable to reuse in future sessions (e.g. "generate a migration for this project", "release flow", "how to add a provider").

Given the conversation below, decide whether it contains such a teachable procedure.

If it does NOT (the session was exploration, Q&A, or a one-off change), respond with exactly: SKIP

If it does, respond with a complete SKILL.md file in this exact format (and nothing else):

---
name: <kebab-case-name, max 5 words>
description: <one sentence: what the skill does and when to use it, max 200 chars>
---

# <Title>

<Step-by-step instructions, written for an AI agent to follow.
Be concrete: exact commands, file paths, and checks from THIS session.
Include verification steps. Max 40 lines.>

Rules:
- Extract the GENERAL procedure, not session-specific noise
- Prefer the corrected/final approach if the session contained trial and error
- Use only facts from the conversation; never invent commands`;

function extractText(message: AssistantMessage): string {
	return message.content
		.filter((c): c is { type: "text"; text: string } => c.type === "text")
		.map((c) => c.text)
		.join("");
}

function serializeConversation(messages: readonly AgentMessage[]): string {
	const lines: string[] = [];
	for (const msg of messages) {
		if (!("role" in msg)) continue;
		const m = msg as { role: string; content: unknown };
		if (m.role === "user" && typeof m.content === "string") {
			lines.push(`User: ${m.content}`);
		} else if (m.role === "user" && Array.isArray(m.content)) {
			const text = (m.content as { type: string; text?: string }[])
				.filter((c) => c.type === "text")
				.map((c) => c.text ?? "")
				.join("\n");
			if (text) lines.push(`User: ${text}`);
		} else if (m.role === "assistant" && Array.isArray(m.content)) {
			const blocks = m.content as { type: string; text?: string; name?: string; arguments?: unknown }[];
			const text = blocks
				.filter((c) => c.type === "text")
				.map((c) => c.text ?? "")
				.join("\n");
			if (text) lines.push(`Assistant: ${text.slice(0, 2000)}`);
			const toolCalls = blocks.filter((c) => c.type === "toolCall");
			for (const t of toolCalls) {
				const args = typeof t.arguments === "object" ? JSON.stringify(t.arguments).slice(0, 300) : "";
				lines.push(`  [Tool: ${t.name} ${args}]`);
			}
		}
	}
	return lines.slice(-150).join("\n");
}

export interface ExtractedSkill {
	name: string;
	content: string;
	filePath: string;
}

export interface ExtractSkillResult {
	skill?: ExtractedSkill;
	skipped: boolean;
	error?: string;
}

/** Parse the model output: validate frontmatter and extract the skill name. */
export function parseSkillOutput(output: string): { name: string; content: string } | undefined {
	const trimmed = output.trim();
	if (!trimmed.startsWith("---")) return undefined;
	const nameMatch = trimmed.match(/^name:\s*([a-z0-9][a-z0-9-]*)\s*$/m);
	const descriptionMatch = trimmed.match(/^description:\s*(.+)$/m);
	if (!nameMatch || !descriptionMatch) return undefined;
	return { name: nameMatch[1], content: `${trimmed}\n` };
}

/** Find a directory name that doesn't collide with an existing skill. */
export function resolveSkillDir(baseDir: string, name: string): string {
	let candidate = join(baseDir, name);
	let suffix = 2;
	while (existsSync(candidate)) {
		candidate = join(baseDir, `${name}-${suffix}`);
		suffix++;
	}
	return candidate;
}

/**
 * Extract a reusable skill from a session's messages and write it to the
 * user's skills directory. Returns the created skill, or skipped=true when
 * the session contains no teachable procedure.
 */
export async function extractSkillFromSession(options: {
	messages: readonly AgentMessage[];
	model: Model<Api>;
	apiKey: string;
	headers?: Record<string, string>;
	nameHint?: string;
}): Promise<ExtractSkillResult> {
	const { messages, model, apiKey, headers, nameHint } = options;

	const conversation = serializeConversation(messages);
	if (!conversation.trim()) {
		return { skipped: true };
	}

	const hint = nameHint ? `\n\nThe user wants the skill to be about: ${nameHint}` : "";

	try {
		const result = await completeSimple(
			model,
			{
				systemPrompt: SKILL_EXTRACTION_PROMPT,
				messages: [
					{
						role: "user",
						content: `<conversation>\n${conversation}\n</conversation>${hint}`,
						timestamp: Date.now(),
					},
				],
			},
			{ apiKey, headers },
		);

		const output = extractText(result).trim();
		if (!output || output === "SKIP") {
			return { skipped: true };
		}

		const parsed = parseSkillOutput(output);
		if (!parsed) {
			return { skipped: false, error: "Model produced an invalid skill format" };
		}

		const skillDir = resolveSkillDir(getLearnedSkillsDir(), parsed.name);
		mkdirSync(skillDir, { recursive: true });
		const filePath = join(skillDir, "SKILL.md");
		writeFileSync(filePath, parsed.content, "utf-8");

		return { skipped: false, skill: { name: parsed.name, content: parsed.content, filePath } };
	} catch (error) {
		return { skipped: false, error: error instanceof Error ? error.message : "Skill extraction failed" };
	}
}
